import React, { useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap"
import { formatDate } from "helpers/misc_helpers"
import {
  SubmitTraderOffer,
  UpdateTraderOffer,
  UploadChatAttachment,
} from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import TraderRequestChat from "./TraderRequestChat"

const statusColors = {
  open: "success",
  evaluating: "warning",
  in_progress: "primary",
  completed: "secondary",
  cancelled: "danger",
}

const statusLabels = {
  open: "Open",
  evaluating: "Evaluating",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
}

const TraderOfferView = ({ request, myOffer, onRefresh }) => {
  const setGlobalLoading = useLoading()
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [isEditing, setIsEditing] = useState(!myOffer)

  // Der Trader entscheidet je Angebot: pro Zeile ODER Gesamtpreis.
  // Modus aus bestehendem Angebot ableiten (Zeilenpreise vorhanden → per_line).
  const offerHasLinePrices = (myOffer?.lines || []).some(l => l.price != null)
  const [offerMode, setOfferMode] = useState(
    offerHasLinePrices ? "per_line" : myOffer?.totalPrice != null ? "bulk" : "per_line"
  )

  // Bulk-Felder
  const [bulkPrice, setBulkPrice] = useState(
    myOffer?.totalPrice ?? ""
  )
  const [currency, setCurrency] = useState(myOffer?.currency ?? "EUR")
  const [offerNotes, setOfferNotes] = useState(myOffer?.notes ?? "")

  const [offerFileURL, setOfferFileURL] = useState(myOffer?.fileURL ?? "")
  const [uploadingFile, setUploadingFile] = useState(false)

  // Per-line: Map von partID → { price, currency }
  const buildInitialLines = () => {
    const map = {}
    if (myOffer?.lines) {
      myOffer.lines.forEach(l => {
        map[l.traderRequestPartID] = {
          price: l.price ?? "",
          currency: l.currency ?? "EUR",
        }
      })
    }
    return map
  }
  const [lineValues, setLineValues] = useState(buildInitialLines)

  const updateLine = (partID, field, value) => {
    setLineValues(prev => ({
      ...prev,
      [partID]: { ...(prev[partID] || { price: "", currency: "EUR" }), [field]: value },
    }))
  }

  // CSV-Export
  const handleExport = () => {
    const headers = ["Part Number", "Manufacturer", "Quantity", "Datecode", "Internal Part", "Description", "Notes"]
    const rows = (request.parts || []).map(p =>
      [p.partNumber, p.manufacturer, p.quantity, p.datecode || "", p.internalPart || "", p.description || "", p.notes || ""]
        .map(v => (String(v).includes(",") ? `"${v}"` : v))
        .join(",")
    )
    const csv = [headers.join(","), ...rows].join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `trader-request-${request.id}-parts.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const buildOfferPayload = () => {
    if (offerMode === "bulk") {
      return {
        totalPrice: bulkPrice !== "" ? parseFloat(bulkPrice) : null,
        currency,
        notes: offerNotes,
        fileURL: offerFileURL || null,
        lines: [],
      }
    }
    // per_line: Gesamtpreis aus Zeilensumme berechnen
    const lines = (request.parts || []).map(part => ({
      traderRequestPartID: part.id,
      price:
        lineValues[part.id]?.price !== ""
          ? parseFloat(lineValues[part.id]?.price)
          : null,
      currency: lineValues[part.id]?.currency || "EUR",
    }))
    const total = lines.reduce((sum, l) => {
      const requestedQty = (request.parts || []).find(p => p.id === l.traderRequestPartID)?.quantity || 0
      return sum + ((l.price || 0) * (requestedQty || 0))
    }, 0)
    return {
      totalPrice: total > 0 ? total : null,
      currency,
      notes: offerNotes,
      fileURL: offerFileURL || null,
      lines,
    }
  }

  const handleSubmit = async () => {
    setError("")
    setSuccess("")
    setGlobalLoading(true)
    try {
      const payload = buildOfferPayload()
      if (myOffer) {
        await UpdateTraderOffer(request.id, myOffer.id, payload)
        setSuccess("Offer updated successfully.")
      } else {
        await SubmitTraderOffer(request.id, payload)
        setSuccess("Offer submitted successfully.")
      }
      setIsEditing(false)
      onRefresh()
    } catch (err) {
      console.error(err)
      setError("Failed to submit offer. Please try again.")
    }
    setGlobalLoading(false)
  }

  const isOpen = request.status === "open" || request.status === "evaluating"

  // Welche Positionen hat der Requester von diesem Trader tatsächlich beauftragt?
  // Sichtbar erst, sobald das eigene Offer ausgewählt wurde (Status 'selected').
  const showOrderedHighlight =
    !!myOffer &&
    myOffer.status === "selected" &&
    ["in_progress", "completed"].includes(request.status)
  const orderedPartIDs = new Set(
    showOrderedHighlight
      ? (myOffer.lines || [])
          .filter(l => l.isSelected === 1 || l.isSelected === true)
          .map(l => l.traderRequestPartID)
      : []
  )

  const ALLOWED_OFFER_FILE_EXT = [".csv", ".pdf", ".xlsx", ".xls", ".png", ".jpg", ".jpeg"]

  const handleFileUpload = async (file) => {
    if (!file) return
    setError("")
    setSuccess("")
    const name = (file.name || "").toLowerCase()
    if (!ALLOWED_OFFER_FILE_EXT.some(ext => name.endsWith(ext))) {
      setError("Allowed file types: CSV, PDF, Excel or image.")
      return
    }
    setUploadingFile(true)
    try {
      const fd = new FormData()
      fd.append("file", file)
      // reuse chat-attachments storage bucket; group by request id
      fd.append("id", String(request.id))
      const res = await UploadChatAttachment(fd)
      if (res?.url) {
        setOfferFileURL(res.url)
        setSuccess("File uploaded successfully.")
      } else {
        setError("File upload failed.")
      }
    } catch (e) {
      console.error(e)
      setError("File upload failed. Please try again.")
    }
    setUploadingFile(false)
  }

  return (
    <>
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}

      {/* Request Header */}
      <Card className="mb-3">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">{request.title}</h5>
              <small className="text-muted">
                Received: {formatDate(request.dateCreated)}
              </small>
            </Col>
            <Col xs="auto">
              <Badge color={statusColors[request.status] || "secondary"} className="fs-6">
                {statusLabels[request.status] || request.status}
              </Badge>
            </Col>
          </Row>
          {request.notes && (
            <p className="text-muted mt-2 mb-0">{request.notes}</p>
          )}
        </CardBody>
      </Card>

      {/* Parts List */}
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              <i className="bx bx-list-ul me-1"></i>
              Parts ({request.parts?.length || 0})
            </h6>
            <Button color="secondary" size="sm" outline onClick={handleExport}>
              <i className="bx bx-download me-1"></i> Export CSV
            </Button>
          </div>

          {offerMode === "per_line" && isEditing ? (
            <>
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              Prices are interpreted as <strong>price per piece</strong>. The total is calculated as price × requested quantity.
            </p>
            
            {/* Per-line: Preiseingabe direkt in der Tabelle */}
            <div className="table-responsive">
              <Table className="table table-bordered table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Part Number</th>
                    <th>Manufacturer</th>
                    <th>Requested Qty</th>
                    <th>Datecode</th>
                    <th>Price per Piece</th>
                    <th>Currency</th>
                  </tr>
                </thead>
                <tbody>
                  {(request.parts || []).map(part => (
                    <tr key={part.id}>
                      <td>{part.partNumber}</td>
                      <td>{part.manufacturer}</td>
                      <td>{part.quantity}</td>
                      <td>{part.datecode || "—"}</td>
                      <td>
                        <Input
                          type="number"
                          bsSize="sm"
                          min={0}
                          step="0.0001"
                          value={lineValues[part.id]?.price ?? ""}
                          onChange={e =>
                            updateLine(part.id, "price", e.target.value)
                          }
                          placeholder="0.0000"
                        />
                      </td>
                      <td>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={lineValues[part.id]?.currency ?? "EUR"}
                          onChange={e =>
                            updateLine(part.id, "currency", e.target.value)
                          }
                        >
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                          <option value="GBP">GBP</option>
                          <option value="YEN">YEN</option>
                        </Input>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            </>
          ) : (
            /* Read-only Parts-Tabelle */
            (() => {
              // Zeilenpreise des eigenen Angebots je Position (falls vorhanden)
              const linePriceByPartID = new Map(
                (myOffer?.lines || [])
                  .filter(l => l.price != null)
                  .map(l => [l.traderRequestPartID, l])
              )
              const showPriceColumn = linePriceByPartID.size > 0
              return (
            <div className="table-responsive">
              {showOrderedHighlight && (
                <div className="mb-2 text-muted" style={{ fontSize: 12 }}>
                  <i className="bx bx-info-circle me-1"></i>
                  Highlighted rows show the positions ordered by the requester.
                </div>
              )}
              <Table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Part Number</th>
                    <th>Manufacturer</th>
                    <th>Quantity</th>
                    {showPriceColumn && <th>Price per Piece</th>}
                    <th>Datecode</th>
                    <th>Internal Part</th>
                    <th>Description</th>
                    {showOrderedHighlight && <th>Status</th>}
                  </tr>
                </thead>
                <tbody>
                  {(request.parts || []).map(part => {
                    const isOrdered = orderedPartIDs.has(part.id)
                    const dim = showOrderedHighlight && !isOrdered
                    return (
                      <tr
                        key={part.id}
                        className={
                          showOrderedHighlight && isOrdered
                            ? "table-success"
                            : dim
                            ? "text-muted"
                            : ""
                        }
                      >
                        <td>{part.partNumber}</td>
                        <td>{part.manufacturer}</td>
                        <td>{part.quantity}</td>
                        {showPriceColumn && (
                          <td>
                            {linePriceByPartID.has(part.id)
                              ? `${parseFloat(
                                  linePriceByPartID.get(part.id).price
                                ).toLocaleString("de-DE", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })} ${
                                  linePriceByPartID.get(part.id).currency || "EUR"
                                }`
                              : "—"}
                          </td>
                        )}
                        <td>{part.datecode || "—"}</td>
                        <td>{part.internalPart || "—"}</td>
                        <td>{part.description || "—"}</td>
                        {showOrderedHighlight && (
                          <td>
                            {isOrdered ? (
                              <Badge color="success">Ordered</Badge>
                            ) : (
                              <span className="text-muted" style={{ fontSize: 12 }}>
                                Not ordered
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
              )
            })()
          )}
        </CardBody>
      </Card>

      {/* Offer Form */}
      {isOpen && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="bx bx-dollar me-1"></i>
                {myOffer ? "My Offer" : "Submit Offer"}
              </h6>
              {myOffer && !isEditing && (
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => setIsEditing(true)}
                >
                  <i className="bx bx-edit me-1"></i> Edit Offer
                </Button>
              )}
            </div>

            {myOffer && !isEditing ? (
              /* Read-only Offer Summary */
              <table className="table table-bordered table-sm">
                <tbody>
                  {offerMode === "bulk" && (
                    <tr>
                      <td className="fw-bold" style={{ width: "30%" }}>
                        Total Price
                      </td>
                      <td>
                        {myOffer.totalPrice != null
                          ? `${parseFloat(myOffer.totalPrice).toLocaleString(
                              "de-DE",
                              { minimumFractionDigits: 2, maximumFractionDigits: 4 }
                            )} ${myOffer.currency}`
                          : "—"}
                      </td>
                    </tr>
                  )}
                  {offerMode === "per_line" && (
                    <tr>
                      <td className="fw-bold">Total (calculated)</td>
                      <td>
                        {(() => {
                          const total = (myOffer.lines || []).reduce((sum, l) => {
                            const part = (request.parts || []).find(p => p.id === l.traderRequestPartID)
                            return sum + ((l.price || 0) * (part?.quantity || 0))
                          }, 0)
                          return total > 0
                            ? `${total.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${myOffer.currency}`
                            : "—"
                        })()}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="fw-bold">Notes</td>
                    <td>{myOffer.notes || "—"}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Submitted</td>
                    <td>{formatDate(myOffer.dateCreated)}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              /* Offer Form */
              <Form>
                <FormGroup row>
                  <Label sm={3}>Pricing</Label>
                  <Col sm={9}>
                    <div className="d-flex gap-4 mt-1">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="offerModePerLine"
                          checked={offerMode === "per_line"}
                          onChange={() => setOfferMode("per_line")}
                        />
                        <label className="form-check-label" htmlFor="offerModePerLine">
                          Price per Line
                          <small className="d-block text-muted">
                            Enter a price for each part above
                          </small>
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id="offerModeBulk"
                          checked={offerMode === "bulk"}
                          onChange={() => setOfferMode("bulk")}
                        />
                        <label className="form-check-label" htmlFor="offerModeBulk">
                          Bulk Total
                          <small className="d-block text-muted">
                            One total price for all parts
                          </small>
                        </label>
                      </div>
                    </div>
                  </Col>
                </FormGroup>
                {offerMode === "bulk" && (
                  <FormGroup row>
                    <Label sm={3}>Total Price</Label>
                    <Col sm={6}>
                      <Input
                        type="number"
                        min={0}
                        step="0.0001"
                        value={bulkPrice}
                        onChange={e => setBulkPrice(e.target.value)}
                        placeholder="0.0000"
                      />
                    </Col>
                    <Col sm={3}>
                      <Input
                        type="select"
                        value={currency}
                        onChange={e => setCurrency(e.target.value)}
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="YEN">YEN</option>
                      </Input>
                    </Col>
                  </FormGroup>
                )}
                <FormGroup row>
                  <Label sm={3}>Notes</Label>
                  <Col sm={9}>
                    <Input
                      type="textarea"
                      rows={3}
                      value={offerNotes}
                      onChange={e => setOfferNotes(e.target.value)}
                      placeholder="Optional notes..."
                    />
                  </Col>
                </FormGroup>
                <FormGroup row className="mt-2">
                  <Label sm={3}>Offer File</Label>
                  <Col sm={9}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Input
                        type="file"
                        accept=".csv,text/csv,.pdf,application/pdf,.xlsx,.xls,image/png,image/jpeg"
                        onChange={e => handleFileUpload(e.target.files?.[0])}
                        disabled={uploadingFile}
                      />
                      {uploadingFile && (
                        <span className="text-primary">
                          <i className="bx bx-loader bx-spin me-1"></i>Uploading…
                        </span>
                      )}
                      {!uploadingFile && offerFileURL && (
                        <span className="text-success">
                          <i className="bx bx-check-circle me-1"></i>
                          <a href={offerFileURL} target="_blank" rel="noreferrer">
                            View uploaded file
                          </a>
                        </span>
                      )}
                    </div>
                    <small className="text-muted">
                      Optional attachment for your offer (CSV, PDF, Excel or image).
                    </small>
                    {error && <div className="text-danger mt-1" style={{ fontSize: 13 }}>{error}</div>}
                    {success && <div className="text-success mt-1" style={{ fontSize: 13 }}>{success}</div>}
                  </Col>
                </FormGroup>

                <div className="d-flex justify-content-end gap-2">
                  {myOffer && (
                    <Button
                      color="secondary"
                      outline
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button color="success" onClick={handleSubmit}>
                    <i className="bx bx-send me-1"></i>
                    {myOffer ? "Update Offer" : "Submit Offer"}
                  </Button>
                </div>
              </Form>
            )}
          </CardBody>
        </Card>
      )}

      {/* Status wenn nicht mehr offen */}
      {!isOpen && request.status !== "in_progress" && request.status !== "completed" && (
        <Alert color="secondary">
          This request is no longer accepting offers (status: <strong>{statusLabels[request.status] || request.status}</strong>).
        </Alert>
      )}

      {/* Chat nach Offer-Annahme (nur wenn dieses Trader-Angebot ausgewählt wurde) */}
      {["in_progress", "completed"].includes(request.status) && myOffer?.status === "selected" && (
        <TraderRequestChat requestId={request.id} />
      )}
    </>
  )
}

export default TraderOfferView
